import { useEffect, useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Minus, Trash2, ShoppingCart, X, MessageCircle, CheckCircle2, AlertCircle, ArrowRight } from "lucide-react";
import { api, formatApiError } from "@/api";
import { useCart } from "@/context/CartContext";
import { useSite, waLinkFromNumber } from "@/context/SiteContext";

const BUSINESS_TYPES = [
  "Restaurant", "Cloud Kitchen", "Hotel", "Cafe",
  "Caterer", "QSR", "Distributor", "Manufacturer", "Franchise Chain",
];
const CONSUMPTION = [
  "Less than 50 Kg", "50–100 Kg", "100–500 Kg", "500–1000 Kg", "1000+ Kg",
];

export default function RawMaterialSupply() {
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [activeCat, setActiveCat] = useState("all");
  const [cartOpen, setCartOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [c, p] = await Promise.all([api.get("/categories"), api.get("/products")]);
        setCategories(c.data);
        setProducts(p.data);
      } catch (_e) {
        // ignore
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const visible = useMemo(
    () => (activeCat === "all" ? products : products.filter((p) => p.category === activeCat)),
    [activeCat, products],
  );

  return (
    <div data-testid="page-raw-material" className="bg-cream">
      <section className="pt-32 pb-12 md:pt-40 md:pb-16 relative overflow-hidden">
        <div className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full bg-[var(--k-olive-soft)] opacity-35 blur-[100px]" />
        <div className="container-k px-6 md:px-12 relative z-10">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="max-w-4xl">
            <div className="font-mono-label text-[var(--k-green)] mb-5">Raw Material Supply</div>
            <h1 data-testid="rms-headline" className="font-serif-display text-5xl md:text-7xl text-[var(--k-text)] leading-[1.02]">
              Industrial-grade kitchen{" "}
              <em className="green-gradient-text not-italic">ingredients</em>.
            </h1>
            <p className="mt-7 text-lg text-[var(--k-text-2)] max-w-2xl leading-relaxed">
              Browse the KOSTRA B2B catalogue. Add products to your cart and
              send your <strong>Request For Quote</strong> directly to our
              WhatsApp desk — pricing within 24 hours.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Category tabs */}
      <section className="bg-cream pb-8" data-testid="rms-categories">
        <div className="container-k px-6 md:px-12">
          <div className="flex gap-2 overflow-x-auto pb-2 -mx-2 px-2">
            <CategoryTab active={activeCat === "all"} onClick={() => setActiveCat("all")} testId="rms-cat-all">
              All Products
            </CategoryTab>
            {categories.map((c) => (
              <CategoryTab key={c.slug} active={activeCat === c.slug} onClick={() => setActiveCat(c.slug)} testId={`rms-cat-${c.slug}`}>
                {c.name}
              </CategoryTab>
            ))}
          </div>
        </div>
      </section>

      {/* Product grid */}
      <section className="bg-cream pb-24" data-testid="rms-grid-section">
        <div className="container-k px-6 md:px-12">
          {loading ? (
            <div className="text-center py-20 text-[var(--k-muted)] font-mono-label" data-testid="rms-loading">Loading catalogue…</div>
          ) : visible.length === 0 ? (
            <div className="text-center py-20 text-[var(--k-muted)] font-mono-label">No products in this category yet.</div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-testid="rms-grid">
              {visible.map((p, i) => (
                <ProductCard key={p.id} product={p} index={i} />
              ))}
            </div>
          )}
        </div>
      </section>

      <CartButton onClick={() => setCartOpen(true)} />
      <CartDrawer open={cartOpen} onClose={() => setCartOpen(false)} />
    </div>
  );
}

function CategoryTab({ active, onClick, children, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`whitespace-nowrap px-5 py-2.5 rounded-full font-mono-label text-[0.65rem] border transition-all ${
        active
          ? "border-[var(--k-green)] bg-[var(--k-green)] text-[var(--k-bg)]"
          : "border-[var(--k-line)] text-[var(--k-text-2)] hover:border-[var(--k-green)] hover:text-[var(--k-green)]"
      }`}
    >
      {children}
    </button>
  );
}

function ProductCard({ product, index }) {
  const [pack, setPack] = useState(product.pack_sizes?.[0] || "1Kg");
  const [qty, setQty] = useState(1);
  const [added, setAdded] = useState(false);
  const { add } = useCart();

  const handleAdd = () => {
    add(product, pack, qty);
    setAdded(true);
    setTimeout(() => setAdded(false), 1600);
  };

  return (
    <motion.article
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.5, delay: (index % 8) * 0.04 }}
      data-testid={`product-card-${product.id}`}
      className="organic-card !p-0 overflow-hidden flex flex-col"
    >
      <div className="relative h-44 overflow-hidden bg-[var(--k-bg-2)]">
        <img
          src={product.image}
          alt={product.name}
          loading="lazy"
          className="w-full h-full object-cover transition-transform duration-700 hover:scale-105"
          onError={(e) => { e.currentTarget.src = "https://images.unsplash.com/photo-1546833999-b9f581a1996d?w=1200&q=85"; }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent" />
      </div>
      <div className="p-5 flex flex-col flex-1">
        <h3 className="font-serif-display text-xl text-[var(--k-text)] leading-tight mb-1" data-testid={`product-name-${product.id}`}>
          {product.name}
        </h3>
        <p className="text-[var(--k-muted)] text-xs leading-relaxed line-clamp-2 mb-3">
          {product.description}
        </p>

        {(product.rate_per_kg || product.amount) && (
          <div className="flex items-baseline gap-3 mb-3 pb-3 border-b border-[var(--k-line-2)]" data-testid={`product-price-${product.id}`}>
            {product.rate_per_kg && (
              <div>
                <span className="font-serif-display text-xl text-[var(--k-green)]">{product.rate_per_kg}</span>
                <span className="font-mono-label text-[var(--k-muted)] text-[0.55rem] ml-1">/kg</span>
              </div>
            )}
            {product.amount && (
              <div className="font-mono-label text-[var(--k-muted)] text-[0.6rem] ml-auto">
                {product.amount}
              </div>
            )}
          </div>
        )}

        <div className="grid grid-cols-3 gap-2 mb-4 text-[0.6rem] font-mono-label">
          <Spec label="MOQ" value={product.moq} />
          <Spec label="Shelf" value={product.shelf_life} />
          <Spec label="Storage" value={product.storage} />
        </div>

        <div className="mt-auto space-y-3">
          <div className="flex flex-wrap gap-1.5">
            {(product.pack_sizes || []).map((s) => (
              <button
                key={s}
                onClick={() => setPack(s)}
                data-testid={`pack-${product.id}-${s}`}
                className={`text-[0.6rem] font-mono-label px-2.5 py-1 rounded-full border transition-colors ${
                  pack === s
                    ? "border-[var(--k-green)] bg-[var(--k-green)] text-[var(--k-bg)]"
                    : "border-[var(--k-line)] text-[var(--k-text-2)]"
                }`}
              >
                {s}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <QtyControl qty={qty} onDec={() => setQty(Math.max(1, qty - 1))} onInc={() => setQty(qty + 1)} testId={`qty-${product.id}`} />
            <button
              onClick={handleAdd}
              data-testid={`add-to-cart-${product.id}`}
              className={`flex-1 inline-flex items-center justify-center gap-2 py-2.5 rounded-full font-mono-label tracking-wider text-[0.65rem] transition-colors ${
                added ? "bg-[var(--k-olive)] text-[var(--k-bg)]" : "bg-[var(--k-green)] text-[var(--k-bg)] hover:bg-[#2D4220]"
              }`}
            >
              {added ? "Added ✓" : <><Plus className="w-3.5 h-3.5" /> Add to Cart</>}
            </button>
          </div>
        </div>
      </div>
    </motion.article>
  );
}

function Spec({ label, value }) {
  return (
    <div className="leading-tight">
      <div className="text-[var(--k-muted)] text-[0.5rem]">{label}</div>
      <div className="text-[var(--k-text)] mt-0.5 truncate">{value || "—"}</div>
    </div>
  );
}

function QtyControl({ qty, onDec, onInc, testId }) {
  return (
    <div className="flex items-center border border-[var(--k-line)] rounded-full px-1 py-1 gap-1" data-testid={testId}>
      <button onClick={onDec} className="w-7 h-7 rounded-full hover:bg-[var(--k-bg-2)] flex items-center justify-center" aria-label="Decrease">
        <Minus className="w-3 h-3 text-[var(--k-text)]" />
      </button>
      <span className="font-mono-label text-[var(--k-text)] text-xs min-w-[20px] text-center">{qty}</span>
      <button onClick={onInc} className="w-7 h-7 rounded-full hover:bg-[var(--k-bg-2)] flex items-center justify-center" aria-label="Increase">
        <Plus className="w-3 h-3 text-[var(--k-text)]" />
      </button>
    </div>
  );
}

function CartButton({ onClick }) {
  const { count } = useCart();
  if (count === 0) return null;
  return (
    <motion.button
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      onClick={onClick}
      data-testid="rms-cart-fab"
      className="fixed bottom-24 left-6 md:bottom-6 md:right-24 md:left-auto z-[140] flex items-center gap-3 bg-[var(--k-green)] text-[var(--k-bg)] px-5 py-3.5 rounded-full shadow-xl hover:bg-[#2D4220] transition-colors"
    >
      <ShoppingCart className="w-4 h-4" />
      <span className="font-mono-label text-[0.65rem] tracking-wider">View Cart</span>
      <span className="font-serif-display text-base bg-[var(--k-bg)] text-[var(--k-green)] w-7 h-7 rounded-full flex items-center justify-center" data-testid="rms-cart-fab-count">{count}</span>
    </motion.button>
  );
}

function CartDrawer({ open, onClose }) {
  const { items, update, remove, clear } = useCart();
  const { content } = useSite();

  const [form, setForm] = useState({
    company_name: "", gst_number: "", contact_person: "", mobile: "", email: "",
    city: "", business_type: BUSINESS_TYPES[0], monthly_consumption: CONSUMPTION[0],
  });
  const [status, setStatus] = useState({ state: "idle", msg: "" });
  const upd = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const buildWaMessage = () => {
    const lines = items.map((i) => `• ${i.name} — ${i.pack_size} × ${i.quantity}`).join("\n");
    return (
      `Hello Team KOSTRA Hospitality,\n\nI would like pricing for:\n\n${lines}\n\n` +
      `Company Name: ${form.company_name}\nGST: ${form.gst_number || "—"}\nContact Person: ${form.contact_person}\n` +
      `Mobile: ${form.mobile}\nEmail: ${form.email}\nCity: ${form.city}\n` +
      `Business Type: ${form.business_type}\nMonthly Requirement: ${form.monthly_consumption}\n\n` +
      `Please share your best B2B rates.\n\nThank You.`
    );
  };

  const submit = async (e) => {
    e.preventDefault();
    if (items.length === 0) {
      setStatus({ state: "error", msg: "Your cart is empty." });
      return;
    }
    setStatus({ state: "loading", msg: "" });
    try {
      await api.post("/leads/cart-rfq", {
        ...form,
        items: items.map((i) => ({
          product_id: i.product_id,
          name: i.name,
          pack_size: i.pack_size,
          quantity: i.quantity,
        })),
      });
      const waUrl = waLinkFromNumber(content.whatsapp, buildWaMessage());
      window.open(waUrl, "_blank", "noopener,noreferrer");
      setStatus({ state: "success", msg: "RFQ saved. WhatsApp opened in a new tab." });
      clear();
    } catch (err) {
      setStatus({ state: "error", msg: formatApiError(err?.response?.data?.detail) });
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[160]"
            onClick={onClose}
          />
          <motion.aside
            initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="fixed top-0 right-0 bottom-0 w-full md:w-[520px] z-[170] bg-[var(--k-bg)] shadow-2xl flex flex-col"
            data-testid="cart-drawer"
          >
            <div className="flex items-center justify-between px-6 py-5 border-b border-[var(--k-line)]">
              <div>
                <div className="font-mono-label text-[var(--k-gold)] text-[0.6rem]">Your B2B Cart</div>
                <div className="font-serif-display text-2xl text-[var(--k-text)] mt-1">Request For Quote</div>
              </div>
              <button onClick={onClose} className="w-9 h-9 rounded-full border border-[var(--k-line)] flex items-center justify-center hover:bg-[var(--k-bg-2)]" data-testid="cart-close">
                <X className="w-4 h-4 text-[var(--k-text)]" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-5 space-y-3">
              {items.length === 0 ? (
                <div className="text-center py-16 text-[var(--k-muted)] font-mono-label" data-testid="cart-empty">Cart is empty</div>
              ) : (
                items.map((it) => (
                  <div key={it.key} className="organic-card !p-4 flex gap-4 items-center" data-testid={`cart-item-${it.product_id}`}>
                    <img src={it.image} alt={it.name} className="w-16 h-16 rounded-lg object-cover bg-[var(--k-bg-2)]" />
                    <div className="flex-1 min-w-0">
                      <div className="font-serif-display text-base text-[var(--k-text)] truncate">{it.name}</div>
                      <div className="font-mono-label text-[var(--k-muted)] text-[0.55rem] mt-0.5">{it.pack_size}</div>
                    </div>
                    <QtyControl qty={it.quantity} onDec={() => update(it.key, Math.max(1, it.quantity - 1))} onInc={() => update(it.key, it.quantity + 1)} testId={`cart-qty-${it.product_id}`} />
                    <button onClick={() => remove(it.key)} className="text-[var(--k-muted)] hover:text-red-500 transition-colors" aria-label="Remove" data-testid={`cart-remove-${it.product_id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))
              )}

              {items.length > 0 && (
                <form onSubmit={submit} className="mt-8 pt-6 border-t border-[var(--k-line)]" data-testid="cart-rfq-form">
                  <div className="font-mono-label text-[var(--k-gold)] mb-4">Your Details</div>
                  <div className="grid grid-cols-1 gap-y-4">
                    <Field label="Company Name *"><input className="kostra-input" required value={form.company_name} onChange={upd("company_name")} data-testid="rfq-input-company" /></Field>
                    <Field label="GST Number (optional)"><input className="kostra-input" value={form.gst_number} onChange={upd("gst_number")} data-testid="rfq-input-gst" /></Field>
                    <Field label="Contact Person *"><input className="kostra-input" required value={form.contact_person} onChange={upd("contact_person")} data-testid="rfq-input-contact" /></Field>
                    <Field label="Mobile *"><input className="kostra-input" type="tel" required value={form.mobile} onChange={upd("mobile")} placeholder="+91 99999 00000" data-testid="rfq-input-mobile" /></Field>
                    <Field label="Email *"><input className="kostra-input" type="email" required value={form.email} onChange={upd("email")} data-testid="rfq-input-email" /></Field>
                    <Field label="City *"><input className="kostra-input" required value={form.city} onChange={upd("city")} data-testid="rfq-input-city" /></Field>
                    <Field label="Business Type">
                      <select className="kostra-input cursor-pointer" value={form.business_type} onChange={upd("business_type")} data-testid="rfq-select-business" style={{ background: "var(--k-card)" }}>
                        {BUSINESS_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </Field>
                    <Field label="Monthly Consumption">
                      <select className="kostra-input cursor-pointer" value={form.monthly_consumption} onChange={upd("monthly_consumption")} data-testid="rfq-select-consumption" style={{ background: "var(--k-card)" }}>
                        {CONSUMPTION.map((t) => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </Field>
                  </div>

                  {status.state === "success" && <StatusBanner ok msg={status.msg} testId="rfq-form-success" />}
                  {status.state === "error" && <StatusBanner msg={status.msg} testId="rfq-form-error" />}

                  <button type="submit" disabled={status.state === "loading"} className="mt-7 w-full inline-flex justify-center items-center gap-2 bg-[#25D366] hover:bg-[#1ebd5a] text-white py-4 rounded-full font-mono-label tracking-wider text-[0.7rem] transition-colors" data-testid="rfq-send-whatsapp">
                    <MessageCircle className="w-4 h-4" /> Send Cart to WhatsApp <ArrowRight className="w-4 h-4" />
                  </button>
                </form>
              )}
            </div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}

function Field({ label, children }) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="font-mono-label text-[var(--k-muted)]">{label}</span>
      {children}
    </label>
  );
}

function StatusBanner({ ok, msg, testId }) {
  return (
    <div data-testid={testId} className={`mt-5 flex items-start gap-3 p-3 rounded ${ok ? "border border-[var(--k-olive)] bg-[rgba(143,166,120,0.12)]" : "border border-red-300 bg-red-50"}`}>
      {ok ? <CheckCircle2 className="w-4 h-4 text-[var(--k-green)] mt-0.5" /> : <AlertCircle className="w-4 h-4 text-red-600 mt-0.5" />}
      <p className="text-[var(--k-text)] text-xs">{msg}</p>
    </div>
  );
}
